import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { queryForContactLists } from '../../graphql/contact-lists/query-for-contact-lists';
import { getContactList } from '../../graphql/contact-lists/get-contact-list';
import { createContactList } from '../../graphql/contact-lists/create-contact-list';
import { updateContactList } from '../../graphql/contact-lists/update-contact-list';
import { getExternalContactList } from '../../graphql/contact-lists/get-external-contact-list';
import { updateExternalContactList } from '../../graphql/contact-lists/update-external-contact-list';
import { createExternalContactList } from '../../graphql/contact-lists/create-external-contact-list';
import { deleteExternalContactList } from '../../graphql/contact-lists/delete-external-contact-list';
import { queryForExternalContactLists } from '../../graphql/contact-lists/query-for-external-contact-lists';
import { getContactListHistories } from '../../graphql/contact-lists/get-contact-list-histories';
import { getExternalContactListHistories } from '../../graphql/contact-lists/get-external-contact-list-histories';
import { getContactListsForContact } from '../../graphql/contact-lists/get-contact-lists-for-contact';
import { deleteContactList } from '../../graphql/contact-lists/delete-contact-list';
import { getContactListMembers } from '../../graphql/contact-lists/get-contact-list-members';
import { getContactListForFacility } from '../../graphql/contact-lists/get-contact-list-for-facility';
import { getManagementContactListForFacility } from '../../graphql/contact-lists/get-management-contact-list-for-facility';
import { createContactListSchedule } from '../../graphql/contact-lists/create-contact-list-schedule';
import { deleteContactListSchedule } from '../../graphql/contact-lists/delete-contact-list-schedule';
import { updateContactListSchedule } from '../../graphql/contact-lists/edit-contact-list-schedule';
import { getContactListSchedules } from '../../graphql/contact-lists/get-contact-list-schedules';
import { importContactListSchedule } from '../../graphql/contact-lists/import-contact-list-schedule';
import { ContactListScheduleImport } from '../../model/contacts/contact-list-schedule-import';
import { BaseAppSyncService } from '../base-app-sync.service';
import {
  ContactList,
  ContactListConnection,
  ContactListSortOrQuery,
} from '../../model/contacts/contact-list';
import { ContactListBuilderService } from '../builder/contact-list-builder.service';
import { ConfigContactListItem } from '../../model/common/config-item';
import { getContactListsForDropdown } from '../../graphql/contact-lists/get-contact-lists-for-dropdown';
import { AdditionalResourceListType } from '../../model/contacts/additional-resource-list-type';
import { getAdditionalResourceListTypes } from '../../graphql/contact-lists/get-additional-resource-list-types';
import { getFacilityContactLists } from 'src/app/graphql/contact-lists/get-facility-contact-lists';
import { FacilityContactListsResponse } from 'src/app/model/contacts/facility-contact-lists-response';
import { Audit } from 'src/app/model/common/audit';
import { buildAuditHistory } from 'src/app/core/functions/common-functions';
import {
  ContactListScheduleInput,
  Schedule,
} from 'src/app/model/contacts/contact-list-schedule';
import { MemberCode } from 'src/app/model/contacts/member-code';
import { getMemberCodes } from 'src/app/graphql/contact-lists/get-member-codes';
import { ContactListMember } from 'src/app/model/contacts/contact-list-member';
import {
  ExternalContactList,
  ExternalContactListConnection,
  ExternalContactListSortOrQuery,
} from 'src/app/model/contacts/external-contact-list';
import { bulkDeleteSchedules } from 'src/app/graphql/contact-lists/bulk-delete-schedules';
@Injectable({
  providedIn: 'root',
})
export class ContactListApiService extends BaseAppSyncService {
  private _contactListBuilder = inject(ContactListBuilderService);

  getContactListsForContact(id: string): Observable<(ContactList | null)[]> {
    return this.query(
      getContactListsForContact,
      { id },
      (items: any[]) =>
        items.map((i) => this._contactListBuilder.buildContactList(i)),
      'getContactListsForContact'
    );
  }

  getContactListsForDropdown(): Observable<ConfigContactListItem[]> {
    return this.query(
      getContactListsForDropdown,
      {},
      (items: any[]) =>
        items.map((i) =>
          this._contactListBuilder.buildContactListForDropdown(i)
        ),
      'getContactListsForDropdown',
      'cache-first'
    );
  }

  getExternalContactList(id: string): Observable<ExternalContactList> {
    return this.query(
      getExternalContactList,
      { id },
      (i: any) => this._contactListBuilder.buildExternalContactList(i),
      'getExternalContactList'
    );
  }

  createContactList(contactListInput: any): Observable<ContactList | null> {
    return this.mutate(
      createContactList,
      { input: contactListInput },
      (i: any) => this._contactListBuilder.buildContactList(i),
      'createContactList'
    );
  }

  createExternalContactList(
    contactListInput: any
  ): Observable<ExternalContactList> {
    return this.mutate(
      createExternalContactList,
      { input: contactListInput },
      (i: any) => this._contactListBuilder.buildExternalContactList(i),
      'createExternalContactList'
    );
  }

  updateContactList(id: string, input: any): Observable<ContactList | null> {
    return this.mutate(
      updateContactList,
      {
        id,
        input,
      },
      (i: any) => this._contactListBuilder.buildContactList(i),
      'updateContactList'
    );
  }

  updateExternalContactList(
    id: string,
    contactListInput: any
  ): Observable<ExternalContactList> {
    return this.mutate(
      updateExternalContactList,
      { id, input: contactListInput },
      (i: any) => this._contactListBuilder.buildExternalContactList(i),
      'updateExternalContactList'
    );
  }

  deleteExternalContactList(id: string): Observable<boolean> {
    return this.mutate(
      deleteExternalContactList,
      { id },
      (i: any) => i.value,
      'deleteExternalContactList'
    );
  }

  queryForContactLists(
    limit: number,
    page: number,
    query: ContactListSortOrQuery,
    sort: ContactListSortOrQuery
  ): Observable<ContactListConnection> {
    return this.query(
      queryForContactLists,
      { limit, page, query, sort },
      (i: any) => this._contactListBuilder.buildContactListConnection(i),
      'queryForContactLists'
    );
  }

  queryForExternalContactLists(
    limit: number,
    page: number,
    query: ExternalContactListSortOrQuery,
    sort: ExternalContactListSortOrQuery
  ): Observable<ExternalContactListConnection> {
    return this.query(
      queryForExternalContactLists,
      { limit, page, query, sort },
      (i: any) =>
        this._contactListBuilder.buildExternalContactListConnection(i),
      'queryForExternalContactLists'
    );
  }

  getContactListHistories(id: string): Observable<Audit[]> {
    return this.query(
      getContactListHistories,
      { id },
      (histories: any[]) => buildAuditHistory(histories),
      'getContactListHistories'
    );
  }

  getExternalContactListHistories(id: string): Observable<Audit[]> {
    return this.query(
      getExternalContactListHistories,
      { id },
      (histories: any[]) => buildAuditHistory(histories),
      'getExternalContactListHistories'
    );
  }

  getContactListMembers(id: string): Observable<ContactListMember[]> {
    return this.query(
      getContactListMembers,
      { id },
      (items: any[]) =>
        items.map((i) => this._contactListBuilder.buildContactListMember(i)),
      'getContactListMembers'
    );
  }

  deleteContactList(id: string): Observable<boolean> {
    return this.mutate(
      deleteContactList,
      { id },
      (i: any) => i,
      'deleteContactList'
    );
  }

  getContactListForFacility(
    facilityId: String
  ): Observable<ContactList | null> {
    return this.query(
      getContactListForFacility,
      { facilityId },
      (item: any) => this._contactListBuilder.buildContactList(item),
      'getContactListForFacility'
    );
  }

  getManagementContactListForFacility(
    facilityId: String
  ): Observable<ContactList | null> {
    return this.query(
      getManagementContactListForFacility,
      { facilityId },
      (item: any) => this._contactListBuilder.buildContactList(item),
      'getManagementContactListForFacility'
    );
  }

  createContactListSchedule(
    contactListScheduleInput: ContactListScheduleInput
  ): Observable<Schedule> {
    return this.mutate(
      createContactListSchedule,
      { input: contactListScheduleInput },
      (i: any) => this._contactListBuilder.buildContactListSchedule(i),
      'createContactListSchedule'
    );
  }

  deleteContactListSchedule(id: string): Observable<boolean> {
    return this.mutate(
      deleteContactListSchedule,
      { id },
      (i: any) => i,
      'deleteContactListSchedule'
    );
  }

  updateContactListSchedule(
    contactListScheduleInput: ContactListScheduleInput
  ): Observable<Schedule> {
    return this.mutate(
      updateContactListSchedule,
      { input: contactListScheduleInput },
      (i: any) => this._contactListBuilder.buildContactListSchedule(i),
      'updateContactListSchedule'
    );
  }

  // This function does NOT build the contact list schedule and will not
  // return the appropriate time converted items. When I tried to do that
  // the contact list schedule page broke entirely with items showing up on
  // every day of the calendar instead of the appropriate ones.
  getContactListSchedules(contactListId: String): Observable<any[]> {
    return this.query(
      getContactListSchedules,
      { contactListId },
      (items: any[]) => items,
      'getContactListSchedules'
    );
  }

  importContactListSchedule(
    contactListId: String,
    schedules: ContactListScheduleImport[]
  ): Observable<Schedule[]> {
    return this.mutate(
      importContactListSchedule,
      { contactListId: contactListId, schedules: schedules },
      (items: any[]) =>
        items.map((s) => this._contactListBuilder.buildContactListSchedule(s)),
      'importContactListSchedule'
    );
  }

  getContactList(id: string): Observable<ContactList | null> {
    return this.query(
      getContactList,
      { id },
      (item: any) => this._contactListBuilder.buildContactList(item),
      'getContactList'
    );
  }

  getAdditionalResourceTypes(): Observable<AdditionalResourceListType[]> {
    return this.query(
      getAdditionalResourceListTypes,
      {},
      (items: any[]) =>
        items.map((i) =>
          this._contactListBuilder.buildAdditionalResourceListType(i)
        ),
      'getAdditionalResourceListTypes',
      'cache-first'
    );
  }

  getFacilityContactLists(
    facilityId: string
  ): Observable<FacilityContactListsResponse> {
    return this.query(
      getFacilityContactLists,
      { facilityId },
      (item: any) => item as FacilityContactListsResponse,
      'getFacilityContactLists'
    );
  }

  getMemberCodes(): Observable<MemberCode[]> {
    return this.query(
      getMemberCodes,
      {},
      (i: any[]) => i.map((i) => this._contactListBuilder.buildMemberCode(i)),
      'getMemberCodes'
    );
  }

  bulkDeleteSchedules(
    contactListId: string,
    startDate: Date,
    endDate: Date
  ): Observable<boolean> {
    return this.mutate(
      bulkDeleteSchedules,
      {
        contactListId,
        startDate: startDate.toLocaleDateString('en-us', {
          year: 'numeric',
          month: '2-digit',
          day: 'numeric',
        }),
        endDate: endDate.toLocaleDateString('en-us', {
          year: 'numeric',
          month: '2-digit',
          day: 'numeric',
        }),
      },
      (i: any) => i,
      'bulkDeleteSchedules'
    );
  }
}
